import { api } from 'restaurantAdmin/api';
import { toISODateFormat } from '@shared/utils/dateFormatters';

interface AvailabilityListing {
  iconName: string;
  id: string;
  name: string;
  publicName: string;
  price: number;
  floorPlanId: string;
}

export interface AdminAvailabilityData {
  guestCount: number;
  isBiddable: boolean;
  isBuyable: boolean;
  listing: AvailabilityListing;
  purchasePrice: number;
  serviceWindow: string;
  time: string;
  date: string;
  isBestAvailable: boolean;
  isFeaturedUpgrade: boolean;
  flaggedWhenSelected?: boolean;
}

export interface HostBookReservationPayload {
  customPrice?: number;
  date: string; // '2022-03-07
  expectedCancellationPolicyId: string | null;
  firstName: string;
  guestCount: number;
  guestId?: string;
  staffNote?: string;
  lastName: string;
  listingId: string;
  listingPrice: number;
  paymentMethodId?: string;
  phone?: string; // '+15555555555'
  restaurantId: string;
  time: string; // '00:00:00
  messageGuest: boolean;
  isBestAvailable: boolean;
  guestTags?: { id: string | null; title: string }[];
}

export interface HostBookMultiTableReservationPayload {
  guestId?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  price?: number;
  date: string;
  guestCount: number;
  floorPlanTableIds: string[];
  paymentMethodId?: string;
  time: string;
  staffNote?: string;
  duration: number;
  messageGuest: boolean;
  guestTags?: { id: string | null; title: string }[];
}

export interface OverlappingReservation {
  id: string;
  guestFirstName: string;
  guestLastName: string;
  reservationTime: string;
  isVIP: boolean;
  isPurchased: boolean;
}

export const getAvailabilities = (
  restaurantId: string,
  date: Date,
  guestCount: number,
  excludeReservationId?: string,
): Promise<AdminAvailabilityData[]> => {
  const searchParams = new URLSearchParams({ guestCount: String(guestCount) });

  if (excludeReservationId) {
    searchParams.append('excludeReservationId', excludeReservationId);
  }

  return api.get(
    `/admin/restaurants/${restaurantId}/availabilities/${toISODateFormat(
      date,
    )}?${searchParams.toString()}`,
  );
};

export const hostBookReservation = (
  payload: HostBookReservationPayload,
): Promise<void> =>
  api.post(`/restaurants/${payload.restaurantId}/reservations`, payload);

export interface AdminSendPayLinkPayload {
  date: string;
  time: string;
  guestCount: number;
  listingId: string;
  listingPrice: number;
  isBestAvailable: boolean;
  expectedCancellationPolicyId: string | null;
  firstName: string;
  lastName: string;
  phone: string;
  guestId?: string;
  guestTags?: { id: string | null; title: string }[];
  staffNote?: string;
  customPrice?: number;
}

export interface AdminSendPayLinkResponse {
  reservationId: string;
  payLinkUrl: string;
}

export const adminSendPayLink = (
  restaurantId: string,
  payload: AdminSendPayLinkPayload,
): Promise<AdminSendPayLinkResponse> =>
  api.post(`/restaurants/${restaurantId}/reservations/pay-link`, payload);

export const hostBookMultiTableReservation = (
  restaurantId: string,
  payload: HostBookMultiTableReservationPayload,
): Promise<void> =>
  api.post(`/restaurants/${restaurantId}/reservations/large-party`, payload);

export const checkMultiTableReservationForOverlaps = (
  restaurantId: string,
  payload: Pick<
    HostBookMultiTableReservationPayload,
    'date' | 'time' | 'duration' | 'floorPlanTableIds'
  > & { excludeReservationId?: string },
): Promise<{
  wouldOverlap: boolean;
  overlappingReservations: OverlappingReservation[];
}> =>
  api.post(
    `/restaurants/${restaurantId}/reservations/large-party-overlap-check`,
    payload,
  );
